import { RootPage } from '../page';
import $api from '../../api';
import languages from '../../utils/languages';
import AppearanceSettingsView from './appearanceSettingsView';
import { ContextHelpDesign, ContextHelpModel, SegmentModel } from '../../models/Gen/TrackableBaseModels';
import * as siteConfig from '../../site-config/trackableConfig';
import RulesDescriptor from '../../components/descriptors/rulesDescriptor';
import { openEditor } from '../../common/editorUtils';
import SegmentationSelector from '../../components/segmentationSelector';
import { listenToPageDirty, resolvePageDirty } from '../../utils/changeTracking';
import ReactSegmentationSelector from '../../../react/descriptors-components/SegmenationSelector';
import { createDefaultRulesDescriptor } from '../../utils/core';
import ImportExport from "legacy/pages/imports-exports/importExport";

class ContextHelpDetailView extends RootPage {
    constructor() {
        super();

        this.ReactSegmentationSelector = ReactSegmentationSelector;

        this.isDirty = ko.observable();

        RulesDescriptor(this);
        SegmentationSelector(this);

        this.contextHelp = ko.observable();
        this.contextHelpDesign = ko.observable();

        this.allTours = ko.observable();

        this.editorOpen = ko.observable(false);

        this.segments = ko.observableArray();
        this.segmentationSelected = ko.observable();

        this.setActiveTab('settings');

        this.allSegmentsRaw = this.getServerSideData('segments');
        this.allToursRaw = this.getServerSideData('allTours');
        this.contextHelpRaw = this.getServerSideData('contextHelp');

        this.resetUsername = ko.observable();

        this.segmentRaw = ko.observable(this.contextHelpRaw.segmentId);
        this.rulesRaw = ko.observable(this.contextHelpRaw.descriptorObject ? this.contextHelpRaw.descriptorObject.rules : []);

        listenToPageDirty((ev) => {
            if (ev.fieldName == 'ContextHelpModel.lastUpdatedAt') return;
            if (ev.fieldName == 'ContextHelpModel.publishStatus') return;

            this.isDirty(true);
        })
    }

    onSegmentationTypeChanged(type) {
        this.segmentRaw(null);
        this.rulesRaw(createDefaultRulesDescriptor());
        this.isDirty(true);
    }

    onSegmentSelected(segment) {
        this.segmentRaw(segment);
        this.isDirty(true);
    }

    onRulesSelected(rules) {
        this.rulesRaw(rules);
        this.isDirty(true);
    }

    onAllUsersSelected() {
        this.segmentRaw(null);
        this.rulesRaw(createDefaultRulesDescriptor());
        this.isDirty(true);
    }

    onInit() {
        this.allTours(this.getServerSideData('allTours'));

        siteConfig.ContextHelpsGroupDescriptor.prototype._ModelExtender = function (model, p, pp, mappingOptions) {
            mappingOptions.rules = {
                create: function (options) {
                    return new siteConfig.RulesDescriptor(options.data);
                }
            };
        };
        ContextHelpModel.prototype._ModelExtender = function (model, p, pp, mappingOptions) {
            mappingOptions.descriptorObject = {
                create: function (options) {
                    return new siteConfig.ContextHelpsGroupDescriptor(options.data);
                }
            };
        };

        this.contextHelp(new ContextHelpModel(this.getServerSideData('contextHelp')));

        for (const segment of this.getServerSideData('segments')) {
            this.segments.push(new SegmentModel(segment));
        }
    }

    importExport() {
        this.showDialog(ImportExport(this), {
            entityTypeName: 'context-help', entityId: this.contextHelp().id(), doneCallback: () => {
                window.location.reload();
            }
        });
    }

    deleteHelp() {
        if (confirm('Really?')) {
            this.startSubmitting('delete');

            $api.contextHelp.delete(
                { projectCode: auth.project.UniqueCode, id: this.contextHelp().id() },
                {},
                () => {
                    this.stopSubmitting('delete');
                    window.location.href = '/' + auth.project.UniqueCode + '/contexthelp/overview';
                }
            );
        }
    }

    saveSettings(callback) {
        var jsContextHelp = this.contextHelp().toJS();
        var contextHelpCopy = JSON.parse(JSON.stringify(jsContextHelp));

        contextHelpCopy.descriptorObject = null;

        contextHelpCopy.segmentId = this.segmentRaw();

        var rules = jsContextHelp.descriptorObject.rules;

        this.startSubmitting('saveSettings');
        $api.contextHelp.saveSettings(
            { projectCode: auth.project.UniqueCode, id: this.contextHelp().id() },
            { contextHelp: contextHelpCopy, rules: this.rulesRaw() },
            (resp) => {
                this.stopSubmitting('saveSettings');
                this.showToast('Saved.');
                this.isDirty(false);
                resolvePageDirty();
                this.contextHelp().publishStatus(resp.publishStatus);
                typeof callback === 'function' && callback();
            }
        );
    }

    publish(callback) {
        this.startSubmitting('publishCheck');

        $api.contextHelp.publish(
            { projectCode: auth.project.UniqueCode, id: this.contextHelp().id() },
            {},
            resp => {
                this.stopSubmitting('publishCheck');

                this.contextHelp().publishStatus(resp.publishStatus);

                typeof callback === 'function' && callback();
            }
        );
    }

    unpublish() {
        this.startSubmitting('unpublish');
        $api.contextHelp.unpublish(
            { projectCode: auth.project.UniqueCode, id: this.contextHelp().id() },
            {},
            resp => {
                this.stopSubmitting('unpublish');
                this.contextHelp().publishStatus(resp.publishStatus);
            }
        );
    }

    editContextHelp() {
        openEditor('hint', this.contextHelp().baseUrl(), this.contextHelp().id(), () => {
            this.editorOpen(false);

            $api.contextHelp.get(
                { projectCode: auth.project.UniqueCode, id: this.contextHelp().id() },
                {},
                resp => {
                    if (resp.lastUpdatedAt > this.contextHelp().lastUpdatedAt()) {
                        TRACK_EVENT('Context Helps', 'Saved');
                    }
                    this.contextHelp().lastUpdatedAt(resp.lastUpdatedAt);
                    this.contextHelp().publishStatus(resp.publishStatus);
                }
            );
        });

        this.editorOpen(true);
    }

    saveAndPublish() {
        this.startSubmitting('save_publish');
        this.saveSettings(() => {
            this.publish(() => {
                this.stopSubmitting('save_publish');
            });
        });
    }

    restartForAll() {
        if (confirm('Really?')) {
            this.startSubmitting('restart');
            $api.contextHelp.restart(
                { projectCode: auth.project.UniqueCode, id: this.contextHelp().id() },
                {},
                (resp) => {
                    this.stopSubmitting('restart');
                    this.showToast('Hints restarted.');
                }
            );
        }
    }

    restartForUser(e) {
        if (confirm('Really?')) {
            this.startSubmitting('restart-for-user');
            $api.contextHelp.restartForUser(
                { projectCode: auth.project.UniqueCode, id: this.contextHelp().id(), username: encodeURIComponent(this.resetUsername()) },
                {},
                (resp) => {
                    this.stopSubmitting('restart-for-user');
                    this.showToast('Hints restarted for the given user.');
                }
            );
        }
    }
}

export default ContextHelpDetailView;
